/* Preview confirmations are browser-only. Production records require the signed backend. */
(() => {
  'use strict';
  const root = () => document.querySelector('[data-community-terms-version]');
  let pending = null;
  const store = () => window.Alpine?.store('communityTerms');
  window.ModlCommunityTerms = {
    ensure: () => store()?.ensure() ?? Promise.resolve(true),
    completeSetup: () => store()?.accept() ?? Promise.resolve(true),
    invalidate: () => store()?.invalidate(),
  };
  document.addEventListener('alpine:init', () => {
    Alpine.store('communityTerms', {
      checked: false, busy: false, error: '', accepted: false,
      epoch: 0, preview: false, enabled: false, version: '', customer: '', key: '',
      init() {
        const data = root()?.dataset || {};
        this.version = data.communityTermsVersion || '';
        this.customer = data.customerId || '';
        this.preview = data.communityTermsPreview === 'true' && data.communityTermsEnabled !== 'true';
        this.enabled = this.preview || data.communityTermsEnabled === 'true';
        this.key = ['modl-terms-preview',data.shopDomain,this.customer,this.version].join(':');
        if (this.preview) {
          try { this.accepted = JSON.parse(localStorage.getItem(this.key) || 'null')?.version === this.version; } catch {}
        }
        window.addEventListener('modl-auth-lost', () => this.invalidate());
      },
      async request(path, body) {
        const base = root()?.dataset.modlProxy || '/apps/modl-community';
        if (!/^\/apps\/[a-z0-9_-]+$/.test(base)) throw new Error('Community connection is not configured.');
        // Use the real signed session, never the sample-message identity adapter.
        const session = await fetch(base + '/api/session', {credentials:'same-origin',cache:'no-store',signal:AbortSignal.timeout(15000)});
        const identity = await session.json().catch(() => ({}));
        if (!session.ok || String(identity.customer_id) !== this.customer || !identity.csrf)
          throw new Error('Please sign in again to continue.');
        const response = await fetch(base + '/api/terms' + path, {
          method: body ? 'POST' : 'GET', credentials:'same-origin', cache:'no-store',
          headers: {'Content-Type':'application/json', ...(body ? {'X-Modl-CSRF':identity.csrf} : {})},
          ...(body ? {body:JSON.stringify(body)} : {}), signal:AbortSignal.timeout(15000),
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Could not save your confirmation. Please try again.');
        if (!data.enabled) throw new Error('Community confirmation is not connected yet. Please try again later.');
        if (data.version !== this.version) throw new Error('The terms have changed. Reload the page to review them.');
        return data;
      },
      invalidate() {
        this.epoch++; this.accepted = false; this.checked = false; this.busy = false;
        pending = null;
      },
      check() {
        if (!this.enabled || this.accepted) return Promise.resolve(true);
        if (!this.customer || this.preview) return Promise.resolve(false);
        if (pending) return pending;
        const epoch = this.epoch;
        this.busy = true; this.error = '';
        const request = this.request('').then(data => {
          if (epoch !== this.epoch) return false;
          this.accepted = data.accepted === true;
          return this.accepted;
        }).catch(error => {
          if (epoch !== this.epoch) return false;
          this.error = 'Could not check your confirmation. Please try again.';
          throw new Error(this.error);
        }).finally(() => {
          if (pending === request) pending = null;
          if (epoch === this.epoch) this.busy = false;
        });
        pending = request;
        return request;
      },
      async ensure() {
        const epoch = this.epoch;
        const accepted = await this.check();
        if (epoch !== this.epoch) return false;
        if (!accepted && this.customer)
          window.dispatchEvent(new CustomEvent('modl-terms-setup-required'));
        return accepted;
      },
      async accept() {
        if (!this.enabled || this.accepted) return true;
        if (!this.customer || this.busy) return false;
        if (!this.checked) {
          this.error = 'Confirm you are 18 or older and agree to the Community Terms to continue.';
          return false;
        }
        this.busy = true; this.error = '';
        const epoch = this.epoch;
        try {
          if (this.preview) {
            // The browser timestamp is only for preview convenience, never legal evidence.
            try { localStorage.setItem(this.key, JSON.stringify({version:this.version,at:new Date().toISOString()})); } catch {}
          } else {
            const data = await this.request('/accept', {version:this.version,accepted:true,age_confirmed:true});
            if (!data.accepted) throw new Error('Confirmation was not saved. Please try again.');
          }
          if (epoch !== this.epoch) return false;
          this.accepted = true;
          return true;
        } catch (e) {
          if (epoch === this.epoch) this.error = e.message || 'Could not save your confirmation. Please try again.';
          return false;
        }
        finally { if (epoch === this.epoch) this.busy = false; }
      },
    });
  });
})();
